import { ComponentProps } from 'react'
import { ColorTokens, styled, theme } from 'stitches.config'

const color = Object.keys(theme.colors).reduce((acc, color) => {
  return { ...acc, [color]: { '&': { color: `$${color}` } } }
}, {}) as { [key in ColorTokens]: any }

const size = Object.keys(theme.fontSizes).reduce((acc, fontSize) => {
  return { ...acc, [fontSize]: { '&': { fontSize: `$${fontSize}`, lineHeight: `$${fontSize}`, letterSpacing: `$${fontSize}` } } }
}, {}) as { [key in keyof typeof theme.fontSizes]: any }

export const Text = styled('span', {
  lineHeight: 1,
  color: 'currentColor',
  margin: 0,
  fontWeight: 400, 
  fontVariantNumeric: 'tabular-nums',

  variants: {
    display: {
      inline: {
        display: 'inline-block',
      },
      block: {
        display: 'block',
      },
      none: {
        display: 'none',
      },
    },
    size: {
      ...size,
      h1: {
        '&': {
          fontSize: '$h3',
          lineHeight: '$h3',
          letterSpacing: '$h3',
          fontFamily: '$display',

          '@bp2': {
            fontSize: '$h1',
            lineHeight: '$h1',
            letterSpacing: '$h1',
          },
        },
      },

      h2: {
        '&': {
          fontSize: '$h4',
          lineHeight: '$h4',
          letterSpacing: '$h4',
          fontFamily: '$display',

          '@bp2': {
            fontSize: '$h2',
            lineHeight: '$h2',
            letterSpacing: '$h2',
          },
        },
      },

      h3: {
        fontFamily: '$display',
        lineHeight: '$h3',
        fontSize: '$h3',
        letterSpacing: '$h3',
      },
    },
    color,
    weight: {
      ultraLight: {
        fontWeight: '$ultraLight',
      },
      extraLight: {
        fontWeight: '$extraLight',
      },
      light: {
        fontWeight: '$light',
      },
      regular: {
        fontWeight: '$regular',
      },
      medium: {
        fontWeight: '$medium',
      },
      semiBold: {
        fontWeight: '$semiBold',
      },
      bold: {
        fontWeight: '$bold',
      },
      extraBold: {
        fontWeight: '$extraBold',
      },
      black: {
        fontWeight: '$black',
      },
    },
    italics: {
      true: {
        fontStyle: 'italic',
      },
    },
    font: {
      primary: {
        fontFamily: '$primary',
      },
      display: {
        fontFamily: '$display',
      },
      label: {
        fontFamily: '$label',
        letterSpacing: '0.06em',
      },
    },
    align: {
      left: {
        textAlign: 'left',
      },
      center: {
        textAlign: 'center',
      },
      right: {
        textAlign: 'right',
      },
      centerBp2: {
        textAlign: 'center',
        '@bp2': {
          textAlign: 'left',
        },
      },
    },

    truncate: {
      true: {
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
    lineThrough: {
      true: {
        textDecoration: 'line-through',
        textDecorationColor: 'currentColor',
        textDecorationThickness: '3px',
      },
      thin: {
        textDecoration: 'line-through',
        textDecorationColor: 'currentColor',
      },
    },
    underline: {
      true: {
        textDecoration: 'underline',
        textDecorationColor: 'currentColor',
        textUnderlineOffset: '0.1em',
      },
    },
    backgroundVariant: {
      current: {
        backgroundColor: 'grey',
      },
    },
    transform: {
      uppercase: {
        textTransform: 'uppercase',
      },
      lowercase: {
        textTransform: 'lowercase',
      },
      capitalize: {
        textTransform: 'capitalize',
      },
    },
  },

  defaultVariants: {
    size: 'body',
    display: 'inline',
    font: 'primary',
  },
})

Text.displayName = 'Text'

export type TextProps = ComponentProps<typeof Text>
